import { useState, useEffect, useRef } from "react"
import {
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Search,
  Briefcase,
  Users,
  X,
  Zap,
  Settings2,
  SlidersHorizontal,
  Check,
  ArrowRight,
} from "lucide-react"
import JobListingService from "../services/JobListingService"
import OrganizationService from "../services/OrganizationService"
import { useAuth } from "../contexts/AuthProvider"
import { useUser } from "../contexts/UserContext"
import { useNavigate } from "react-router-dom"
import ProfileSidebar from "./ProfileSideBar"
import ProfileIncompleteOverlay from "./ProfileIncompleteOverlay"
import NoPermissionOverlay from "./NoPermissionOverlay"
import JobDetailsPanel from "./JobDetailsPanel"

// ── Shared grid column definition ─────────────────────────────────────────────
// Name | Rounds | Candidates | Date | Interview Types | Status | Actions
const GRID_COLS = "1.4fr 0.6fr 0.8fr 1.2fr 1.4fr 0.9fr 1.2fr"

// ── Status badge config ───────────────────────────────────────────────────────
const STATUS_STYLES = {
  active: {
    dot: "bg-emerald-400",
    label: "ACTIVE",
    text: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
  },
  draft: {
    dot: "bg-zinc-400",
    label: "DRAFT",
    text: "text-zinc-500",
    bg: "bg-zinc-50",
    border: "border-zinc-200",
  },
  closed: {
    dot: "bg-zinc-300",
    label: "CLOSED",
    text: "text-zinc-400",
    bg: "bg-zinc-100",
    border: "border-zinc-200",
  },
  on_hold: {
    dot: "bg-amber-400",
    label: "ON HOLD",
    text: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
  },
}
const getStatusStyle = (status) => STATUS_STYLES[status] ?? STATUS_STYLES.draft

// ── Column header ─────────────────────────────────────────────────────────────
const ColHeader = ({ label, sortable }) => (
  <div className="flex items-center gap-1">
    <span
      className="text-xs font-bold uppercase tracking-widest text-zinc-500"
      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
    >
      {label}
    </span>
    {sortable && (
      <span className="flex flex-col">
        <ChevronUp
          size={8}
          className="text-zinc-300 -mb-0.5"
          strokeWidth={2.5}
        />
        <ChevronDown size={8} className="text-zinc-300" strokeWidth={2.5} />
      </span>
    )}
  </div>
)

// ── Table header row ──────────────────────────────────────────────────────────
const TableHeader = () => (
  <div
    className="grid items-center px-6 py-3 border-b border-zinc-200"
    style={{
      gridTemplateColumns: GRID_COLS,
      backgroundColor: "rgb(250, 249, 245)",
    }}
  >
    <ColHeader label="Name" sortable />
    <ColHeader label="Rounds" />
    <ColHeader label="Candidates" />
    <ColHeader label="Date" sortable />
    <ColHeader label="Interview Types" />
    <ColHeader label="Status" />
    <div />
  </div>
)

// ── Department filter dropdown ────────────────────────────────────────────────
const DepartmentFilter = ({ departments, selectedDepts, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const toggle = (dept) => {
    onChange(
      selectedDepts.includes(dept)
        ? selectedDepts.filter((d) => d !== dept)
        : [...selectedDepts, dept],
    )
  }

  const allSelected = selectedDepts.length === 0
  const label = allSelected
    ? "Department"
    : selectedDepts.length === 1
      ? selectedDepts[0]
      : `${selectedDepts.length} selected`

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-bold uppercase tracking-widest transition-all ${
          !allSelected
            ? "border-zinc-900 bg-zinc-900 text-white"
            : "border-zinc-300 bg-white text-zinc-500 hover:border-zinc-500 hover:text-zinc-700"
        }`}
        style={{ borderRadius: 0, fontFamily: "'IBM Plex Mono', monospace" }}
      >
        <SlidersHorizontal size={10} strokeWidth={2.5} />
        {label}
        <ChevronDown
          size={9}
          strokeWidth={2.5}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-1 min-w-[180px] bg-white border border-zinc-200 shadow-lg z-50 py-1"
          style={{ borderRadius: 0 }}
        >
          <button
            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50 transition-colors"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            onClick={() => {
              onChange([])
              setOpen(false)
            }}
          >
            All Departments
            {allSelected && (
              <Check size={10} className="text-zinc-900" strokeWidth={2.5} />
            )}
          </button>
          <div className="border-t border-zinc-100 my-1" />
          {departments.map((dept) => (
            <button
              key={dept}
              className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50 transition-colors"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              onClick={() => toggle(dept)}
            >
              <span className="truncate mr-2">{dept}</span>
              {selectedDepts.includes(dept) && (
                <Check
                  size={10}
                  className="text-zinc-900 flex-shrink-0"
                  strokeWidth={2.5}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Job row ───────────────────────────────────────────────────────────────────
const JobRow = ({
  listing,
  index,
  total,
  onCardClick,
  onSettingsClick,
  navigate,
}) => {
  const status = listing.status ?? "active"
  const style = getStatusStyle(status)

  return (
    <div
      className={`grid items-center px-6 py-3.5 cursor-pointer group transition-colors hover:bg-zinc-50 ${
        index !== total - 1 ? "border-b border-zinc-100" : ""
      }`}
      style={{ gridTemplateColumns: GRID_COLS }}
      onClick={() => onCardClick(listing)}
    >
      {/* Name + ID */}
      <div className="min-w-0 pr-4">
        <p className="text-sm font-semibold text-zinc-900 group-hover:text-zinc-700 transition-colors truncate">
          {listing.role}
        </p>
        <p
          className="text-[10px] text-zinc-400 mt-0.5 truncate"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          {listing.jobId || "—"}
        </p>
      </div>

      {/* Rounds */}
      <div>
        <span
          className="text-sm font-bold text-zinc-700"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          {String(listing.interviewStructure?.rounds?.length ?? 0).padStart(
            2,
            "0",
          )}
        </span>
      </div>

      {/* Candidates */}
      <div className="flex items-center gap-1.5">
        <Users size={11} className="text-zinc-300 flex-shrink-0" />
        <span
          className="text-sm text-zinc-700 font-medium"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          {listing.applicantCount ?? 0}
        </span>
      </div>

      {/* Date */}
      <div>
        <span
          className="text-[13px] text-zinc-500"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          {listing.createdAt
            ? new Date(listing.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "—"}
        </span>
      </div>

      {/* Interview types */}
      {(() => {
        const rounds = listing?.interviewStructure?.rounds ?? []
        const humanAiCount = rounds.filter(
          (r) => r.roundType === "Human+Ai",
        ).length
        const aiCount = rounds.filter((r) => r.roundType === "Ai").length
        const hasAnyRound = humanAiCount > 0 || aiCount > 0

        return hasAnyRound ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            {humanAiCount > 0 && (
              <button
                className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-blue-200 bg-blue-50 text-[9px] font-bold uppercase tracking-widest text-blue-700 hover:bg-blue-100 hover:border-blue-400 transition-colors"
                style={{
                  borderRadius: 0,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(
                    `/jobs/${listing.organizationId}/${listing.id}/add-structure`,
                  )
                }}
              >
                <Users size={8} />
                {humanAiCount} Human+Ai
              </button>
            )}
            {aiCount > 0 && (
              <button
                className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-violet-200 bg-violet-50 text-[9px] font-bold uppercase tracking-widest text-violet-700 hover:bg-violet-100 hover:border-violet-400 transition-colors"
                style={{
                  borderRadius: 0,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(
                    `/jobs/${listing.organizationId}/${listing.id}/add-ai-interview`,
                  )
                }}
              >
                <Zap size={8} />
                {aiCount} AI
              </button>
            )}
          </div>
        ) : (
          <button
            className="inline-flex items-center gap-1 px-1.5 py-0.5 w-fit border border-dashed border-zinc-300 text-[9px] font-bold uppercase tracking-widest text-zinc-400 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white transition-all"
            style={{
              borderRadius: 0,
              fontFamily: "'IBM Plex Mono', monospace",
            }}
            onClick={(e) => {
              e.stopPropagation()
              navigate(
                `/jobs/${listing.organizationId}/${listing.id}/add-structure-wrapper`,
              )
            }}
          >
            <Zap size={8} />
            Configure
          </button>
        )
      })()}

      {/* Status */}
      <div>
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] font-bold ${style.text} ${style.bg} ${style.border}`}
          style={{ borderRadius: 0 }}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`}
          />
          {style.label}
        </span>
      </div>

      {/* Actions */}
      <div
        className="flex items-center justify-end gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-zinc-400 hover:text-zinc-600 transition-all"
          onClick={(e) => {
            e.stopPropagation()
            onSettingsClick(listing)
          }}
        >
          <Settings2 size={11} strokeWidth={2} />
          <span
            className="text-[10px] font-bold uppercase tracking-widest"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            Settings
          </span>
        </button>
        <button
          className="flex items-center gap-1.5 px-2.5 py-1.5  border-zinc-300 text-zinc-700 hover:border-zinc-400 hover:text-zinc-900 transition-all"
          style={{ borderRadius: 0, backgroundColor: "rgb(250, 249, 245)" }}
          onClick={(e) => {
            e.stopPropagation()
            onCardClick(listing)
          }}
        >
          <span
            className="text-[10px] font-bold uppercase tracking-widest"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            Applicants
          </span>
          <ArrowRight size={11} strokeWidth={2} />
        </button>
      </div>
    </div>
  )
}

// ── Shimmer ───────────────────────────────────────────────────────────────────
const JobListingsShimmer = () => (
  <div>
    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((i) => (
      <div
        key={i}
        className="grid items-center px-6 py-4 border-b border-zinc-100"
        style={{ gridTemplateColumns: GRID_COLS }}
      >
        <div className="space-y-1.5 pr-4">
          <div className="h-3.5 w-3/4 bg-zinc-100 animate-pulse" />
          <div className="h-2.5 w-1/3 bg-zinc-50 animate-pulse" />
        </div>
        <div className="h-3 w-8 bg-zinc-100 animate-pulse" />
        <div className="h-3 w-8 bg-zinc-100 animate-pulse" />
        <div className="h-3 w-16 bg-zinc-100 animate-pulse" />
        <div className="h-5 w-20 bg-zinc-100 animate-pulse" />
        <div className="h-5 w-14 bg-zinc-100 animate-pulse" />
        <div className="h-5 w-8 bg-zinc-50 animate-pulse" />
      </div>
    ))}
  </div>
)

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ searchQuery, onClear, onCreate }) => (
  <div className="flex flex-col items-center justify-center py-24 border border-dashed border-zinc-200">
    <div
      className="w-10 h-10 border border-zinc-200 bg-zinc-50 flex items-center justify-center mb-4"
      style={{ borderRadius: 0 }}
    >
      <Briefcase size={16} className="text-zinc-300" />
    </div>
    <p className="text-xs font-bold uppercase tracking-widest text-zinc-900 mb-1">
      {searchQuery ? "No matches found" : "No job listings yet"}
    </p>
    <p className="text-xs text-zinc-400 mb-5">
      {searchQuery
        ? "Try a different search term"
        : "Create your first listing to get started"}
    </p>
    {searchQuery ? (
      <button
        className="text-xs font-bold uppercase tracking-widest text-zinc-600 border border-zinc-300 hover:border-zinc-600 px-4 py-2 transition-colors"
        style={{ borderRadius: 0 }}
        onClick={onClear}
      >
        Clear search
      </button>
    ) : (
      onCreate && (
        <button
          className="text-xs font-bold uppercase tracking-widest text-white bg-zinc-900 hover:bg-zinc-700 px-4 py-2 transition-colors"
          style={{ borderRadius: 0 }}
          onClick={onCreate}
        >
          Create a job
        </button>
      )
    )}
  </div>
)

// ── Error state ───────────────────────────────────────────────────────────────
const ErrorState = ({ error, onRetry }) => (
  <div className="flex flex-col items-center justify-center py-24 border border-dashed border-red-200">
    <div
      className="w-10 h-10 border border-red-100 bg-red-50 flex items-center justify-center mb-4"
      style={{ borderRadius: 0 }}
    >
      <X size={16} className="text-red-300" />
    </div>
    <p className="text-xs font-bold uppercase tracking-widest text-zinc-900 mb-1">
      Something went wrong
    </p>
    <p className="text-xs text-zinc-400 mb-5">{error}</p>
    <button
      className="text-xs font-bold uppercase tracking-widest text-zinc-600 border border-zinc-300 hover:border-zinc-600 px-4 py-2 transition-colors"
      style={{ borderRadius: 0 }}
      onClick={onRetry}
    >
      Try again
    </button>
  </div>
)

// ── Department group ──────────────────────────────────────────────────────────
const DepartmentGroup = ({
  department,
  listings,
  onCardClick,
  onSettingsClick,
  navigate,
  allDepartments,
  selectedDepts,
  onDeptChange,
  showFilter,
}) => {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="mb-8">
      {/* Department heading row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="flex items-center justify-center w-6 h-6 text-zinc-400 hover:text-zinc-700 transition-colors"
            style={{ borderRadius: 0 }}
          >
            {collapsed ? (
              <ChevronRight size={16} strokeWidth={2.5} />
            ) : (
              <ChevronDown size={16} strokeWidth={2.5} />
            )}
          </button>
          <h3 className="text-lg font-bold text-zinc-900">{department}</h3>
          <span
            className="text-[10px] text-zinc-400 font-medium border border-zinc-100 px-1.5 py-0.5 bg-zinc-50"
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              borderRadius: 0,
            }}
          >
            {listings.length}
          </span>
        </div>

        {/* Department filter — only on first group */}
        {showFilter && (
          <DepartmentFilter
            departments={allDepartments}
            selectedDepts={selectedDepts}
            onChange={onDeptChange}
          />
        )}
      </div>

      {/* Table — collapsible */}
      {!collapsed && (
        <div className="bg-white overflow-hidden">
          <TableHeader />
          {listings.map((listing, index) => (
            <JobRow
              key={listing.id}
              listing={listing}
              index={index}
              total={listings.length}
              onCardClick={onCardClick}
              onSettingsClick={onSettingsClick}
              navigate={navigate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const JobListingsTab = () => {
  const { user } = useAuth()
  const { isAdmin, isInterviewer } = useUser()
  const navigate = useNavigate()

  const [jobListings, setJobListings] = useState([])
  const [isLoadingJobListings, setIsLoadingJobListings] = useState(true)
  const [jobListingsError, setJobListingsError] = useState(null)
  const [organization, setOrganization] = useState(null)

  const [searchQuery, setSearchQuery] = useState("")
  const [filteredListings, setFilteredListings] = useState([])
  const [selectedDepts, setSelectedDepts] = useState([])
  const [showProfileIncompleteOverlay, setShowProfileIncompleteOverlay] =
    useState(false)
  const [deletingJobId, setDeletingJobId] = useState(null)

  const [panelOpen, setPanelOpen] = useState(false)
  const [panelListing, setPanelListing] = useState(null)

  useEffect(() => {
    if (organization) {
      setShowProfileIncompleteOverlay(
        !organization.logoUrl || !organization.description,
      )
    }
  }, [organization])

  useEffect(() => {
    const fetch_ = async () => {
      if (!user?.uid) return
      setIsLoadingJobListings(true)
      setJobListingsError(null)
      try {
        const orgData = await OrganizationService.getUserOrganization(user.uid)
        setOrganization(orgData)
        if (orgData) {
          const listings = await JobListingService.getOrganizationJobListings(
            orgData.id,
          )
          setJobListings(listings)
        }
      } catch {
        setJobListingsError(
          "Failed to load job listings. Please try again later.",
        )
      } finally {
        setIsLoadingJobListings(false)
      }
    }
    fetch_()
  }, [user?.uid])

  useEffect(() => {
    let filtered = [...jobListings]
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter((l) =>
        [l.role, l.description, l.department, l.location || "Remote"].some(
          (v) => v?.toLowerCase().includes(q),
        ),
      )
    }
    if (selectedDepts.length > 0) {
      filtered = filtered.filter((l) =>
        selectedDepts.includes(l.department || "Other"),
      )
    }
    setFilteredListings(filtered)
  }, [searchQuery, jobListings, selectedDepts])

  const allDepartments = [
    ...new Set(jobListings.map((l) => l.department || "Other")),
  ].sort()

  const groupedListings = filteredListings.reduce((acc, listing) => {
    const dept = listing.department || "Other"
    if (!acc[dept]) acc[dept] = []
    acc[dept].push(listing)
    return acc
  }, {})
  const sortedDepartments = Object.keys(groupedListings).sort()

  const handleDeleteJobListing = async (listingId) => {
    if (!organization) return
    setDeletingJobId(listingId)
    try {
      await fetch(
        `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${organization.id}/${listingId}`,
        { method: "DELETE", headers: { "Content-Type": "application/json" } },
      )
      setJobListings((prev) => prev.filter((l) => l.id !== listingId))
    } catch (error) {
      console.error("Error deleting job listing:", error)
    } finally {
      setDeletingJobId(null)
    }
  }

  const handleCreateNewJob = () => navigate("/jobs/create")
  const handleEditListing = (listing) => navigate(`/jobs/${listing.id}/edit`)
  const handleJobCardClick = (listing) =>
    navigate(`/jobs/${listing.organizationId}/${listing.id}`)

  const openPanel = (listing) => {
    setPanelListing(listing)
    setPanelOpen(true)
  }
  const closePanel = () => setPanelOpen(false)
  const clearSearch = () => setSearchQuery("")

  return (
    <div
      className="min-h-screen bg-zinc-50"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .hide-scroll::-webkit-scrollbar { display: none; }
        .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="flex">
        {/* Sidebar */}
        <div className="border-r border-zinc-200 h-full bg-white overflow-y-auto min-h-screen">
          <ProfileSidebar activeTab="jobs" setActiveTab={() => {}} />
        </div>

        {/* Main */}
        <div className="flex-1 ml-60 hide-scroll overflow-y-auto">
          <div className="min-h-screen flex flex-col bg-zinc-50 text-zinc-900">
            {/* ── Top bar ── */}
            <div className="bg-white px-8 py-4 flex items-center justify-between border-b border-zinc-200 shrink-0">
              <div className="flex items-center gap-4">
                <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-900">
                  Jobs
                </h2>
                <span
                  className="text-[10px] text-zinc-400 font-medium border border-zinc-100 px-2 py-0.5 bg-zinc-50"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    borderRadius: 0,
                  }}
                >
                  {filteredListings.length}{" "}
                  {filteredListings.length === 1 ? "listing" : "listings"}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* Search */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={13} className="text-zinc-500" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search jobs…"
                    className="pl-8 pr-7 py-1.5 border border-zinc-200 w-56 text-[14px] text-zinc-800 placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors bg-white hover:border-zinc-300"
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      borderRadius: 0,
                    }}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-zinc-300 hover:text-zinc-600 transition-colors"
                      onClick={clearSearch}
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>

                {isAdmin && (
                  <button
                    onClick={handleCreateNewJob}
                    className="flex items-center gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-widest bg-zinc-900 text-white hover:bg-zinc-700 transition-colors border border-zinc-900"
                    style={{ borderRadius: 0 }}
                  >
                    <Plus size={12} strokeWidth={2.5} />
                    New Job
                  </button>
                )}
              </div>
            </div>

            {/* ── Content ── */}
            <div className="flex-1 overflow-y-auto hide-scroll">
              <div className="p-6">
                {isLoadingJobListings ? (
                  <div className="bg-white">
                    <TableHeader />
                    <JobListingsShimmer />
                  </div>
                ) : jobListingsError ? (
                  <ErrorState
                    error={jobListingsError}
                    onRetry={() => window.location.reload()}
                  />
                ) : filteredListings.length === 0 ? (
                  <EmptyState
                    searchQuery={searchQuery}
                    onClear={clearSearch}
                    onCreate={isAdmin ? handleCreateNewJob : null}
                  />
                ) : (
                  sortedDepartments.map((department, idx) => (
                    <DepartmentGroup
                      key={department}
                      department={department}
                      listings={groupedListings[department]}
                      onCardClick={handleJobCardClick}
                      onSettingsClick={openPanel}
                      navigate={navigate}
                      allDepartments={allDepartments}
                      selectedDepts={selectedDepts}
                      onDeptChange={setSelectedDepts}
                      showFilter={idx === 0}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Details panel ── */}
      <JobDetailsPanel
        listing={panelListing}
        organization={organization}
        isOpen={panelOpen}
        onClose={closePanel}
        onEdit={(listing) => {
          closePanel()
          handleEditListing(listing)
        }}
        onDelete={handleDeleteJobListing}
        isDeleting={deletingJobId === panelListing?.id}
        navigate={navigate}
      />

      <ProfileIncompleteOverlay
        isOpen={showProfileIncompleteOverlay}
        onClose={() => navigate("/profile")}
        onComplete={() => navigate("/profile")}
      />

      <NoPermissionOverlay isOpen={isInterviewer} onClose={() => navigate("/interviews")} />
    </div>
  )
}

export default JobListingsTab
